/* Shopify renders discounts; this read-only view never changes the shopper's cart. */
if (!customElements.get('product-cart-discount')) {
  customElements.define('product-cart-discount', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      document.addEventListener('throne:cart-updating', () => {
        this.cartUpdating = true;
        this.invalidate();
      }, options);
      document.addEventListener('throne:cart-updated', () => {
        this.cartUpdating = false;
        this.refresh();
      }, options);
      this.closest('[data-product-section]')?.addEventListener('variant:change', () => {
        this.dataset.variantId = this.closest('product-purchase').querySelector('[name="id"]').value;
        this.refresh();
      }, options);
      window.addEventListener('pageshow', () => this.refresh(), options);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.refresh();
      }, options);
      this.refresh();
    }

    invalidate() {
      this.request?.abort();
      this.querySelector('[data-cart-discount-content]').hidden = true;
      const basePrice = this.querySelector('[data-base-price]');
      if (basePrice) basePrice.hidden = false;
    }

    async refresh() {
      this.invalidate();
      if (this.cartUpdating) return;
      const request = this.request = new AbortController();
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('variant', this.dataset.variantId);
      try {
        const options = { cache: 'no-store', signal: request.signal };
        // Read the native saved code, including codes carried through /discount/CODE
        // redirects. Never infer validity from the URL or store a second copy.
        const [section, cart] = await Promise.allSettled([
          fetch(url, options).then(response => {
            if (!response.ok) throw new Error('Discount refresh failed');
            return response.text();
          }),
          fetch(this.dataset.cartUrl + '.js', options).then(response => {
            if (!response.ok) throw new Error('Cart discount check failed');
            return response.json();
          })
        ]);
        if (request.signal.aborted || !this.isConnected) return;
        let rendered = false;
        if (section.status === 'fulfilled') {
          const next = new DOMParser().parseFromString(section.value, 'text/html').querySelector('product-cart-discount [data-cart-discount-content]');
          if (next) {
            this.querySelector('[data-cart-discount-content]').replaceWith(next);
            const basePrice = this.querySelector('[data-base-price]');
            if (basePrice) basePrice.hidden = next.dataset?.hasPrice === 'true';
            rendered = true;
          }
        }
        if (rendered && cart.status === 'fulfilled') this.showCodes(cart.value);
      } catch {
        // Failed refreshes cannot leave stale savings presented as current.
      }
    }

    showCodes(cart) {
      const content = this.querySelector('[data-cart-discount-content]');
      const notice = this.querySelector('[data-discount-code-content]');
      const list = this.querySelector('[data-discount-code-list]');
      if (!notice || !list) return;
      list.replaceChildren();
      // With an empty cart Shopify can't evaluate codes yet; the header pill
      // (discount-code-notice) covers that state, so the buy box stays quiet.
      const codes = cart.item_count > 0 && Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      // Avoid duplicating code names already present in the rendered savings,
      // but retain codes for other merchandise and shipping offers too.
      let renderedTitles = [];
      try { renderedTitles = JSON.parse(content.dataset?.discountTitles || '[]'); } catch { /* No confirmed names. */ }
      if (!Array.isArray(renderedTitles)) renderedTitles = [];
      const visibleCodes = codes.filter(entry => entry && typeof entry.code === 'string' && entry.code.trim()
        && typeof entry.applicable === 'boolean'
        && (entry.applicable !== true || !renderedTitles.some(title => String(title).toLowerCase() === entry.code.toLowerCase())));
      for (const entry of visibleCodes) {
        const row = document.createElement('li');
        const code = document.createElement('strong');
        code.textContent = entry.code;
        const status = entry.applicable === true ? this.dataset.codeApplied : this.dataset.codeNotApplicable;
        row.append(code, document.createTextNode(' ' + status));
        list.append(row);
      }
      notice.hidden = visibleCodes.length === 0;
      if (visibleCodes.length) content.hidden = false;
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
    }
  });
}
